import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// ant design icons
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// project imports
import useAuth from 'hooks/useAuth';
import { getImpersonationCandidates } from 'services/api/impersonation';

// The list is server-filtered, so typing hits the API. Wait for a pause in
// typing rather than firing per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

const initials = (name, email) =>
  (name || email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

// ==============================|| IMPERSONATE — PICK SOMEONE ||============================== //

/**
 * Choose a person to view the product as.
 *
 * Two steps on purpose. Picking a name is browsing; the second step is where
 * the commitment happens, and it names who and says plainly that the session is
 * recorded. This hands the caller another person's view of a child's record —
 * the last chance to notice the wrong row is worth the extra click.
 *
 * The list comes from GET /impersonation/candidates, which applies exactly the
 * rules POST /start enforces, so nothing offered here can come back refused.
 * An org admin sees their own organization; a platform admin sees everyone.
 */
export default function ImpersonatePickerDialog({ open, onClose, presetUser = null }) {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const { beginImpersonation } = useAuth();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The person picked but not yet confirmed. Null means we are still browsing.
  const [chosen, setChosen] = useState(null);
  const [starting, setStarting] = useState(false);

  // `presetUser` skips the browse step: the caller already has somebody on
  // screen — Check user access, say — and browsing a list to find the person
  // they are already looking at would be a step backwards. They still get the
  // confirm step, because that is where the audit warning lives.

  // Reset between openings, so yesterday's search is not still in the box.
  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebounced('');
      setChosen(null);
      setError(null);
      setStarting(false);
      return;
    }
    if (presetUser) setChosen(presetUser);
  }, [open, presetUser]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!open || presetUser) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    getImpersonationCandidates(debounced ? { q: debounced } : {})
      .then((res) => {
        if (!live) return;
        setItems(res?.items || []);
        setScope(res?.scope || null);
      })
      .catch((err) => {
        if (live) setError(err?.detail || err?.message || t('impersonation.pick.loadFailed'));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // `t` is recreated each render; the effect depends only on the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debounced, presetUser]);

  const confirm = async () => {
    setStarting(true);
    setError(null);
    try {
      // Reloads the app as the target on success, so nothing after this runs.
      await beginImpersonation(chosen.id);
    } catch (err) {
      setError(err?.detail || err?.message || t('impersonation.startFailed'));
      setStarting(false);
    }
  };

  const scopeNote = useMemo(() => {
    if (scope === 'platform') return t('impersonation.pick.scopePlatform');
    if (scope === 'org') return t('impersonation.pick.scopeOrg');
    return null;
  }, [scope, intl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- step two: confirm the person picked ----
  if (chosen) {
    return (
      <Dialog open={open} onClose={starting ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('impersonation.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('impersonation.confirmBody', { email: <strong>{chosen.email || chosen.username}</strong> })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }} color="warning.main">
            {t('impersonation.confirmAudit')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => (presetUser ? onClose?.() : setChosen(null))} disabled={starting}>
            {t(presetUser ? 'impersonation.pick.cancel' : 'impersonation.pick.back')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirm}
            disabled={starting}
            startIcon={starting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t(starting ? 'impersonation.starting' : 'impersonation.confirmAction')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ---- step one: browse ----
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EyeOutlined />
          <span>{t('impersonation.pick.title')}</span>
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('impersonation.pick.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('impersonation.pick.searchPlaceholder')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              )
            }
          }}
        />
        {scopeNote && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            {scopeNote}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1.5, minHeight: 240, maxHeight: 380, overflowY: 'auto' }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : items.length === 0 ? (
            <Stack alignItems="center" spacing={0.5} sx={{ py: 6, px: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {debounced ? t('impersonation.pick.noMatches') : t('impersonation.pick.empty')}
              </Typography>
              {!debounced && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('impersonation.pick.emptyHint')}
                </Typography>
              )}
            </Stack>
          ) : (
            <List disablePadding>
              {items.map((u, i) => (
                <Box key={u.id}>
                  {i > 0 && <Divider component="li" />}
                  <ListItemButton onClick={() => setChosen(u)} sx={{ py: 1.25, borderRadius: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                      <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem' }}>{initials(u.name, u.email)}</Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {u.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                          {u.email}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        {u.roles && <Chip label={u.roles} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />}
                        {/* Only meaningful when the caller can reach past their
                            own organization; otherwise it says the same thing on
                            every row. */}
                        {scope === 'platform' && u.org_name && (
                          <Chip label={u.org_name} size="small" sx={{ height: 22, fontSize: '0.72rem' }} />
                        )}
                      </Stack>
                    </Stack>
                  </ListItemButton>
                </Box>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('impersonation.pick.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}

ImpersonatePickerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // {id, email, username, name} — enough for the confirm step. When given, the
  // browse step and its API call are skipped entirely.
  presetUser: PropTypes.object
};
